import { CITIZENSHIP_KEY } from '../../../hooks/useCandidateProfile'

export const filterOutNewOrDeletedCitizenships = ({ citizenshipsToDelete, regionalCitizenships, newCitizenshipIds }) => {
  let filtered = [...regionalCitizenships]
  if (citizenshipsToDelete && citizenshipsToDelete.length) {
    filtered = filtered.filter(
      regionalCitizenship => !regionalCitizenship.id || !(regionalCitizenship.id && citizenshipsToDelete.includes(regionalCitizenship.id)),
    )
  }

  if (newCitizenshipIds && newCitizenshipIds.length) {
    filtered = filtered.filter(regionalCitizenship => regionalCitizenship.id && !newCitizenshipIds.includes(regionalCitizenship.id))
  }
  return filtered
}

export const formatInitialValues = ({ regionalCitizenshipsData, shouldExcludeNoId }) => {
  let filteredCitizenships = regionalCitizenshipsData.filter(regionalCitizenship => {
    const isCitizen = regionalCitizenship.citizenshipStatus === 'CITIZEN'
    const hasCountry = !!regionalCitizenship.countryName
    return hasCountry && isCitizen
  })

  if (shouldExcludeNoId) {
    filteredCitizenships = filteredCitizenships.filter(regionalCitizenship => regionalCitizenship.id)
  }

  const citizenships = filteredCitizenships.map(regionalCitizenship => ({
    label: regionalCitizenship.countryName,
    value: regionalCitizenship.countryIso2Code,
  }))
  const regionalCitizenships = regionalCitizenshipsData
    .filter(regionalCitizenship => regionalCitizenship.id && regionalCitizenship.citizenshipStatus !== 'CITIZEN')
    .map(regionalCitizenship => ({
      rcId: regionalCitizenship.id,
      ...regionalCitizenship,
      country: {
        label: regionalCitizenship.countryName,
        value: regionalCitizenship.countryIso2Code,
      },
    }))
  return {
    citizenships,
    regionalCitizenships,
  }
}

export const formatFormDataForUpdate = ({ formValues, queryClient, candidateId }) => {
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
  const { regionalCitizenships } = cachedQueryData.candidateProfile
  const formRegionalCitizenships = formValues.regionalCitizenships.filter(regionalCitizenship => regionalCitizenship.id)
  const regionalCitizenshipsToUpdate = formRegionalCitizenships.filter(formRegionalCitizenship => {
    const foundPrevRegionalCitizenship = regionalCitizenships.find(
      regionalCitizenship => regionalCitizenship.id === formRegionalCitizenship.id,
    )
    return (
      foundPrevRegionalCitizenship &&
      (foundPrevRegionalCitizenship.citizenshipStatus !== formRegionalCitizenship.citizenshipStatus ||
        foundPrevRegionalCitizenship.countryIso2Code !== formRegionalCitizenship.countryIso2Code ||
        foundPrevRegionalCitizenship.countryName !== formRegionalCitizenship.countryName)
    )
  })

  return regionalCitizenshipsToUpdate.map(regionalCitizenship => ({
    id: regionalCitizenship.id,
    citizenshipStatus: regionalCitizenship.citizenshipStatus,
    countryName: regionalCitizenship.countryName,
    countryIso2Code: regionalCitizenship.countryIso2Code,
  }))
}

export const updateFormState = ({ formValues, queryClient, candidateId, reset, citizenshipsToDelete }) => {
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
  const citizenshipsToSet = [...formValues.citizenships]

  const existingCitizenships = filterOutNewOrDeletedCitizenships({
    regionalCitizenships: cachedQueryData.candidateProfile.regionalCitizenships,
    citizenshipsToDelete,
  })

  cachedQueryData.candidateProfile.regionalCitizenships = [...existingCitizenships]

  const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
    regionalCitizenship => regionalCitizenship.countryIso2Code,
  )

  citizenshipsToSet.forEach(citizenship => {
    if (!existingCountryCodes.includes(citizenship.value)) {
      cachedQueryData.candidateProfile.regionalCitizenships.push({
        countryName: citizenship.label,
        countryIso2Code: citizenship.value,
        citizenshipStatus: 'CITIZEN',
      })
    }
  })

  queryClient.setQueryData(([CITIZENSHIP_KEY, candidateId], cachedQueryData))
  reset(
    formatInitialValues({
      regionalCitizenshipsData: cachedQueryData.candidateProfile.regionalCitizenships,
      shouldExcludeNoId: false,
    }),
  )
}

export const formatWorkAuthDataForCreate = ({ formValues, queryClient, candidateId }) => {
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
  const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
    regionalCitizenship => regionalCitizenship.countryIso2Code,
  )

  return formValues.regionalCitizenships
    .filter(
      regionalCitizenship =>
        !regionalCitizenship.rcId &&
        regionalCitizenship.citizenshipStatus &&
        regionalCitizenship.countryName &&
        regionalCitizenship.countryIso2Code &&
        !existingCountryCodes.includes(regionalCitizenship.countryIso2Code),
    )
    .map(regionalCitizenship => ({
      candidateProfileId: candidateId,
      citizenshipStatus: regionalCitizenship.citizenshipStatus,
      countryName: regionalCitizenship.countryName,
      countryIso2Code: regionalCitizenship.countryIso2Code,
    }))
}

export const formatCitizenshipDataForCreate = ({ formValues, queryClient, candidateId, allCountries }) => {
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
  const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
    regionalCitizenship => regionalCitizenship.countryIso2Code,
  )

  return formValues.citizenships
    .filter(
      country =>
        country &&
        country.value &&
        (!existingCountryCodes.includes(country.value) ||
          cachedQueryData.candidateProfile.regionalCitizenships.find(
            regionalCitizenship => regionalCitizenship.countryIso2Code === country.value && !regionalCitizenship.id,
          )),
    )
    .map(citizenshipCountry => {
      const foundCountry = allCountries.find(country => country.value === citizenshipCountry.value)
      return {
        citizenshipStatus: 'CITIZEN',
        countryName: citizenshipCountry.label,
        countryIso2Code: foundCountry.value,
        candidateProfileId: candidateId,
      }
    })
}
